import dataProvider from "../../dataProvider";
import { uniq } from "lodash";

/**
 * Processes records for CSV export, chunking data and fetching additional information as needed
 * based on the specified resource type. A callback function is called to track loading progress.
 *
 * @param records - An array of records to process.
 * @param callback - A function to handle the loading state and progress updates.
 * @returns An array containing processed data ready for CSV export.
 */
export const processCcaStudentRenewalRecordsForCsv = async (
  records: any[],
  callback: (progressUpdate: {
    isLoading?: boolean;
    loadedRecordCount: number;
    totalRecords: number;
  }) => void,
) => {
  // Split the records array into chunks of the specified size
  const chunkSize = 50;
  const chunks = Array.from(
    { length: Math.ceil(records.length / chunkSize) },
    (_, i) => records.slice(i * chunkSize, i * chunkSize + chunkSize),
  );

  let processedData: any[] = [];

  // Loop over each chunk to process records in manageable batches
  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
    const chunk = chunks[chunkIndex];

    const studentIds = uniq(chunk.map((record) => record.student.id));
    const { data: students } = await dataProvider.getMany("students", {
      ids: studentIds,
    });

    const adminIds = uniq(
      chunk
        .filter((c) => c.updatedBy?.role === "Admin")
        .map((record) => record.updatedBy.id),
    );
    const { data: admins } = await dataProvider.getMany("admins", {
      ids: adminIds,
    });

    // Process all records within the chunk concurrently
    const chunkResults = await Promise.all(
      chunk.map(async (record, recordIndex) => {
        // Calculate the current record index for progress tracking and
        // update the progress using the callback
        const currentIndex = chunkIndex * chunkSize + recordIndex + 1;
        callback({
          loadedRecordCount: currentIndex,
          totalRecords: records.length,
        });

        const findParent = students.find(
          (student: IStudent) => student.id === record.student.id,
        ).parent;

        const updatedByData = () => {
          if (record.updatedBy === null) {
            return "-";
          }

          if (record.updatedBy.role === "Admin") {
            const data = admins.find(
              (admin: IAdmin) => admin.id === record.updatedBy.id,
            );

            return `${record.updatedBy.role} - ${data?.fullName}`;
          } else if (record.updatedBy.role === "Parent") {
            const data = students.find(
              (student: IStudent) => student.parent.id === record.updatedBy.id,
            );

            return `${record.updatedBy.role} - ${data?.parent?.firstName} ${data?.parent?.lastName}`;
          }
        };

        try {
          return {
            no: currentIndex,
            studentId: record.student.id,
            name: `${record.student.firstName} ${record.student.lastName}`,
            ccaAcademicYear: record.currentCcaAcademicYear.name,
            parent: `${findParent.firstName} ${findParent.lastName}`,
            isMaxLevel: record.isMaxLevel,
            status: record.status,
            updatedBy: updatedByData(),
          };
        } catch {
          throw new Error("Failed to fetch cca student renewal record");
        }
      }),
    );

    // Append processed chunk results to the main processed data array
    processedData = [...processedData, ...chunkResults.flat()];
  }

  // Return the fully processed data for CSV export
  return processedData;
};
